<template>
  <b-card class="seller-commission-card shadow-sm h-100" no-body>
    <div class="seller-commission-header">
      <div class="seller-commission-title">
        <span class="seller-title-icon">
          <b-icon icon="graph-up" />
        </span>
        <div>
          <span class="seller-commission-eyebrow">Meu desempenho</span>
          <h3 class="mb-1">Meta e comissão</h3>
          <span class="seller-commission-period">
            {{ dados?.periodo?.label || "Mês atual" }}
          </span>
        </div>
      </div>
      <span v-if="dados?.regra?.nome" class="seller-rule-badge">
        {{ dados.regra.nome }}
      </span>
    </div>

    <div v-if="loading" class="seller-commission-loading">
      <b-skeleton width="38%" height="18px" />
      <b-skeleton width="58%" height="42px" class="mt-3" />
      <b-skeleton width="100%" height="12px" class="mt-5" />
      <div class="seller-loading-grid mt-5">
        <b-skeleton v-for="item in 3" :key="item" height="92px" />
      </div>
    </div>

    <div
      v-else-if="!dados || !dados.disponivel"
      class="seller-commission-empty"
    >
      <div class="seller-empty-icon">
        <b-icon icon="person-badge" />
      </div>
      <strong>Acompanhamento indisponível</strong>
      <span>
        {{
          dados?.mensagem ||
          "Peça ao administrador para conferir seu vínculo de vendedor."
        }}
      </span>
    </div>

    <template v-else>
      <div class="seller-commission-hero">
        <div class="seller-progress-copy">
          <div>
            <span class="seller-kicker">Vendido no mês</span>
            <strong class="seller-sales-value">
              {{ money(dados.realizado) }}
            </strong>
          </div>
          <div class="text-right">
            <span class="seller-kicker">Meta</span>
            <strong class="seller-target-value">{{ money(dados.meta) }}</strong>
          </div>
        </div>

        <div class="seller-progress-track">
          <div
            class="seller-progress-fill"
            :class="{ completed: atingimento >= 100 }"
            :style="{ width: progressWidth }"
          ></div>
        </div>

        <div class="seller-progress-footer">
          <strong>{{ percent(atingimento) }} da meta</strong>
          <span v-if="dados.faltante_meta > 0">
            Faltam {{ money(dados.faltante_meta) }}
          </span>
          <span v-else class="seller-goal-complete">
            <b-icon icon="check-circle-fill" class="mr-1" />
            Meta alcançada
          </span>
        </div>
      </div>

      <div class="seller-summary-grid">
        <div class="seller-summary-box commission">
          <span>{{
            dados.condicionado_recebimento
              ? "Comissão disponível"
              : "Comissão acumulada"
          }}</span>
          <strong>{{
            money(
              dados.condicionado_recebimento
                ? dados.comissao_disponivel
                : dados.comissao_calculada
            )
          }}</strong>
          <small>{{ currentCommissionLabel }}</small>
        </div>
        <div class="seller-summary-box range">
          <span>Faixa atual</span>
          <strong>{{ currentRangeLabel }}</strong>
          <small>{{ currentRangeDetail }}</small>
        </div>
        <div class="seller-summary-box pace">
          <span>Ritmo para a meta</span>
          <strong>{{ paceLabel }}</strong>
          <small>{{ paceDetail }}</small>
        </div>
      </div>

      <div v-if="dados.condicionado_recebimento" class="seller-financial-note">
        <div>
          <b-icon icon="wallet2" />
          <span>
            <strong>{{ money(dados.comissao_disponivel) }}</strong>
            já disponível
          </span>
        </div>
        <div>
          <b-icon icon="clock-history" />
          <span>
            <strong>
              {{ money(dados.comissao_aguardando_recebimento) }}
            </strong>
            aguardando recebimentos
          </span>
        </div>
      </div>

      <div v-if="nextRange" class="seller-next-range">
        <div class="seller-next-icon">
          <b-icon icon="lightning-fill" />
        </div>
        <div class="seller-next-copy">
          <span>Próximo salto</span>
          <strong>
            {{ percent(nextRange.percentual_meta) }} da meta ·
            {{ rangeCommissionLabel(nextRange) }}
          </strong>
          <small>
            Venda mais {{ money(nextRange.falta_vender) }} e sua comissão
            estimada passa para {{ money(nextRange.comissao_projetada) }}.
          </small>
        </div>
        <div class="seller-next-gain">
          <span>Ganho potencial</span>
          <strong>+ {{ money(nextRange.ganho_adicional) }}</strong>
        </div>
      </div>

      <div v-if="scenarios.length" class="seller-range-section">
        <div class="seller-range-heading">
          <div>
            <strong>Mapa das faixas</strong>
            <span>Veja quanto cada conquista pode render.</span>
          </div>
          <span class="seller-projection-label"
            >Projeção no início da faixa</span
          >
        </div>
        <div class="seller-range-list">
          <div
            v-for="range in scenarios"
            :key="`${range.percentual_meta}-${range.percentual_comissao_vista}-${range.percentual_comissao_prazo}`"
            class="seller-range-item"
            :class="rangeClass(range)"
          >
            <div class="seller-range-status">
              <b-icon :icon="rangeIcon(range)" />
              <span>{{ rangeStatus(range) }}</span>
            </div>
            <strong>{{ percent(range.percentual_meta) }} da meta</strong>
            <span>{{ rangeCommissionLabel(range) }}</span>
            <small>
              {{ money(range.comissao_projetada) }}
            </small>
          </div>
        </div>
      </div>

      <div v-else class="seller-fixed-rule">
        <b-icon icon="percent" />
        <div>
          <strong>Comissão por modalidade</strong>
          <span>
            À vista {{ percent(dados.regra?.percentual_vista) }} · A prazo
            {{ percent(dados.regra?.percentual_prazo) }}
          </span>
        </div>
      </div>
    </template>
  </b-card>
</template>

<script>
export default {
  name: "ComissaoVendedor",
  props: {
    dados: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  computed: {
    atingimento() {
      return Number(this.dados?.atingimento || 0);
    },
    progressWidth() {
      return `${Math.max(0, Math.min(100, this.atingimento))}%`;
    },
    scenarios() {
      return Array.isArray(this.dados?.cenarios) ? this.dados.cenarios : [];
    },
    nextRange() {
      return this.dados?.proxima_faixa || null;
    },
    currentCommissionLabel() {
      const range = this.dados?.faixa_atual;
      if (range) {
        return this.rangeCommissionLabel(range);
      }

      const vista = Number(this.dados?.regra?.percentual_vista || 0);
      const prazo = Number(this.dados?.regra?.percentual_prazo || 0);
      return vista === prazo
        ? `${this.percent(vista)} sobre as vendas`
        : "À vista + a prazo";
    },
    currentRangeLabel() {
      const range = this.dados?.faixa_atual;
      if (!range) return "Em construção";

      return `${this.percent(range.percentual_inicial)}${
        range.percentual_final != null
          ? `–${this.percent(range.percentual_final)}`
          : "+"
      }`;
    },
    currentRangeDetail() {
      const range = this.dados?.faixa_atual;
      if (!range) {
        return this.scenarios.length
          ? "Avance até a primeira faixa"
          : "Regra fixa de comissão";
      }

      return this.rangeCommissionLabel(range);
    },
    paceLabel() {
      if (this.atingimento >= 100) return "Meta batida";

      return `${this.money(this.dados?.ritmo?.necessario_por_dia)}/dia`;
    },
    paceDetail() {
      if (this.atingimento >= 100) return "Continue avançando nas faixas";

      return `${this.dados?.ritmo?.dias_restantes || 0} dias restantes`;
    },
  },
  methods: {
    money(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value) || 0);
    },
    percent(value) {
      const number = Number(value) || 0;
      return `${new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(number)}%`;
    },
    rangeCommissionLabel(range) {
      const vista = Number(
        range?.percentual_comissao_vista ?? range?.percentual_comissao ?? 0
      );
      const prazo = Number(range?.percentual_comissao_prazo ?? vista);

      return vista === prazo
        ? `${this.percent(vista)} de comissão`
        : `Vista ${this.percent(vista)} · prazo ${this.percent(prazo)}`;
    },
    rangeClass(range) {
      return {
        achieved: range.atingida && !range.atual,
        current: range.atual,
        next: range.proxima,
        locked: !range.atingida && !range.proxima,
      };
    },
    rangeStatus(range) {
      if (range.atual) return "Você está aqui";
      if (range.atingida) return "Conquistada";
      if (range.proxima) return `Faltam ${this.money(range.falta_vender)}`;
      return "Próxima etapa";
    },
    rangeIcon(range) {
      if (range.atual) return "geo-alt-fill";
      if (range.atingida) return "check-circle-fill";
      if (range.proxima) return "arrow-up-circle-fill";
      return "lock-fill";
    },
  },
};
</script>

<style scoped>
.seller-commission-card {
  border: 0;
  border-radius: 16px;
  color: #25283d;
  overflow: hidden;
}

.seller-commission-header {
  align-items: flex-start;
  background: radial-gradient(
      circle at 92% 15%,
      rgba(255, 255, 255, 0.22),
      transparent 26%
    ),
    linear-gradient(130deg, #17265f 0%, #344db7 55%, #6254d9 100%);
  color: #fff;
  display: flex;
  justify-content: space-between;
  padding: 22px 24px 18px;
}

.seller-commission-header h3 {
  color: #fff;
  font-size: 1.45rem;
  font-weight: 800;
}

.seller-commission-eyebrow,
.seller-commission-period {
  color: rgba(255, 255, 255, 0.76);
  display: block;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.seller-commission-period {
  font-size: 0.74rem;
  letter-spacing: 0.02em;
  text-transform: none;
}

.seller-rule-badge {
  background: rgba(255, 255, 255, 0.13);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  max-width: 42%;
  overflow: hidden;
  padding: 7px 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seller-commission-loading,
.seller-commission-empty {
  padding: 28px 24px;
}

.seller-loading-grid,
.seller-summary-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.seller-commission-empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 7px;
  justify-content: center;
  min-height: 280px;
  text-align: center;
}

.seller-commission-empty span {
  color: #7e8299;
  max-width: 420px;
}

.seller-empty-icon {
  align-items: center;
  background: #edf0ff;
  border-radius: 50%;
  color: #4b5fd1;
  display: flex;
  font-size: 1.5rem;
  height: 52px;
  justify-content: center;
  margin-bottom: 5px;
  width: 52px;
}

.seller-commission-hero {
  padding: 20px 24px 10px;
}

.seller-progress-copy,
.seller-progress-footer {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
}

.seller-kicker {
  color: #8b90a7;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.seller-sales-value {
  color: #1d2656;
  display: block;
  font-size: 1.85rem;
  font-weight: 850;
  letter-spacing: -0.04em;
}

.seller-target-value {
  color: #555b70;
  display: block;
  font-size: 1rem;
}

.seller-progress-track {
  background: #e9ebf5;
  border-radius: 999px;
  height: 12px;
  margin: 14px 0 8px;
  overflow: hidden;
}

.seller-progress-fill {
  background: linear-gradient(90deg, #42c5ad, #4d6fe8);
  border-radius: inherit;
  height: 100%;
  min-width: 4px;
  transition: width 0.45s ease;
}

.seller-progress-fill.completed {
  background: linear-gradient(90deg, #24b47e, #55ca93);
}

.seller-progress-footer {
  color: #6f758c;
  font-size: 0.78rem;
}

.seller-progress-footer strong {
  color: #3b4262;
}

.seller-goal-complete {
  color: #1f9d6a;
  font-weight: 700;
}

.seller-summary-grid {
  padding: 8px 24px 14px;
}

.seller-summary-box {
  background: #f7f8fc;
  border: 1px solid #eceef6;
  border-radius: 12px;
  min-width: 0;
  padding: 13px 14px;
}

.seller-summary-box > span,
.seller-summary-box small {
  color: #8a90a5;
  display: block;
  font-size: 0.68rem;
}

.seller-summary-box > span {
  font-weight: 700;
  text-transform: uppercase;
}

.seller-summary-box strong {
  color: #252b49;
  display: block;
  font-size: 1.03rem;
  margin: 3px 0 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seller-summary-box.commission {
  background: #effbf8;
  border-color: #d4f3eb;
}

.seller-summary-box.commission strong {
  color: #16876c;
}

.seller-summary-box.range {
  background: #f1f3ff;
  border-color: #e0e4ff;
}

.seller-summary-box.range strong {
  color: #4658c8;
}

.seller-financial-note {
  align-items: center;
  background: #fff9ec;
  border: 1px solid #f8ebca;
  border-radius: 10px;
  color: #7d6940;
  display: flex;
  font-size: 0.76rem;
  gap: 22px;
  justify-content: center;
  margin: 0 24px 14px;
  padding: 9px 12px;
}

.seller-financial-note > div {
  align-items: center;
  display: flex;
  gap: 7px;
}

.seller-next-range {
  align-items: center;
  background: linear-gradient(100deg, #fff4f1, #fff8f3);
  border: 1px solid #ffe0d7;
  border-radius: 13px;
  display: grid;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  margin: 0 24px 15px;
  padding: 12px 14px;
}

.seller-next-icon {
  align-items: center;
  background: #ff705f;
  border-radius: 10px;
  color: #fff;
  display: flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.seller-next-copy > span,
.seller-next-copy > strong,
.seller-next-copy > small,
.seller-next-gain > span,
.seller-next-gain > strong {
  display: block;
}

.seller-next-copy > span,
.seller-next-gain > span {
  color: #a27168;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
}

.seller-next-copy > strong {
  color: #6c3d36;
  font-size: 0.84rem;
}

.seller-next-copy > small {
  color: #936f68;
  margin-top: 1px;
}

.seller-next-gain {
  text-align: right;
}

.seller-next-gain strong {
  color: #e85445;
  font-size: 1rem;
}

.seller-range-section {
  border-top: 1px solid #eef0f6;
  padding: 14px 24px 20px;
}

.seller-range-heading {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.seller-range-heading strong,
.seller-range-heading span {
  display: block;
}

.seller-range-heading span {
  color: #9297aa;
  font-size: 0.7rem;
}

.seller-projection-label {
  text-align: right;
}

.seller-range-list {
  display: grid;
  gap: 9px;
  grid-auto-columns: minmax(132px, 1fr);
  grid-auto-flow: column;
  overflow-x: auto;
  padding-bottom: 3px;
}

.seller-range-item {
  background: #f7f8fb;
  border: 1px solid #eceef4;
  border-radius: 11px;
  min-width: 132px;
  padding: 10px 11px;
}

.seller-range-item > strong,
.seller-range-item > span,
.seller-range-item > small {
  display: block;
}

.seller-range-item > strong {
  color: #303650;
  font-size: 0.82rem;
  margin-top: 6px;
}

.seller-range-item > span {
  color: #777d92;
  font-size: 0.69rem;
}

.seller-range-item > small {
  color: #4c5269;
  font-size: 0.76rem;
  font-weight: 750;
  margin-top: 5px;
}

.seller-range-status {
  align-items: center;
  color: #969bad;
  display: flex;
  font-size: 0.61rem;
  font-weight: 700;
  gap: 5px;
  min-height: 18px;
  text-transform: uppercase;
}

.seller-range-item.achieved {
  background: #eefaf5;
  border-color: #d7f0e5;
}

.seller-range-item.achieved .seller-range-status {
  color: #2b9a73;
}

.seller-range-item.current {
  background: #eef1ff;
  border-color: #8998ee;
  box-shadow: 0 5px 16px rgba(72, 88, 190, 0.13);
}

.seller-range-item.current .seller-range-status,
.seller-range-item.current > small {
  color: #4558c8;
}

.seller-range-item.next {
  background: #fff5ef;
  border-color: #ffc6b5;
}

.seller-range-item.next .seller-range-status,
.seller-range-item.next > small {
  color: #e65d4e;
}

.seller-range-item.locked {
  opacity: 0.66;
}

.seller-fixed-rule {
  align-items: center;
  background: #f5f6fb;
  border-top: 1px solid #eaecf4;
  display: flex;
  gap: 12px;
  margin-top: 2px;
  padding: 14px 24px 18px;
}

.seller-fixed-rule > svg {
  color: #5365d4;
  font-size: 1.25rem;
}

.seller-fixed-rule strong,
.seller-fixed-rule span {
  display: block;
}

.seller-fixed-rule span {
  color: #7e8498;
  font-size: 0.76rem;
}

@media (max-width: 767px) {
  .seller-commission-header,
  .seller-progress-copy,
  .seller-range-heading {
    gap: 10px;
  }

  .seller-rule-badge,
  .seller-projection-label {
    display: none;
  }

  .seller-summary-grid {
    grid-template-columns: 1fr;
  }

  .seller-next-range {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .seller-next-gain {
    grid-column: 2;
    text-align: left;
  }

  .seller-financial-note {
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }
}

/* O card segue a mesma linguagem dos demais widgets do ERP: superfície
   branca, bordas discretas e a identidade da revenda aplicada nos destaques. */
.seller-commission-card {
  --seller-primary: var(--primary-color, #ee7361);
  background: #ffffff;
  border: 1px solid #ebedf3 !important;
  border-radius: 0.42rem;
  color: #3f4254;
  max-width: 100%;
  overflow: hidden;
}

.seller-commission-header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #ebedf3;
  color: #3f4254;
  padding: 1.25rem 1.5rem;
}

.seller-commission-title {
  align-items: center;
  display: flex;
  gap: 0.9rem;
  min-width: 0;
}

.seller-title-icon {
  align-items: center;
  background: color-mix(in srgb, var(--seller-primary) 12%, #ffffff);
  border-radius: 0.42rem;
  color: var(--seller-primary);
  display: flex;
  flex: 0 0 auto;
  font-size: 1.15rem;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.seller-commission-header h3 {
  color: #3f4254;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
}

.seller-commission-eyebrow,
.seller-commission-period {
  display: block;
}

.seller-commission-eyebrow {
  color: var(--seller-primary);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.seller-commission-period {
  color: #7e8299;
  font-size: 0.74rem;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;
}

.seller-rule-badge {
  background: color-mix(in srgb, var(--seller-primary) 10%, #ffffff);
  border: 1px solid color-mix(in srgb, var(--seller-primary) 24%, #ffffff);
  border-radius: 0.42rem;
  color: var(--seller-primary);
  font-size: 0.7rem;
  font-weight: 700;
  max-width: 38%;
  padding: 0.45rem 0.7rem;
}

.seller-commission-loading,
.seller-commission-empty {
  padding: 1.5rem;
}

.seller-empty-icon {
  background: color-mix(in srgb, var(--seller-primary) 12%, #ffffff);
  color: var(--seller-primary);
}

.seller-commission-hero {
  background: color-mix(in srgb, var(--seller-primary) 3%, #ffffff);
  padding: 1.25rem 1.5rem 0.8rem;
}

.seller-kicker {
  color: #7e8299;
  font-size: 0.68rem;
}

.seller-sales-value {
  color: #3f4254;
  font-size: 1.65rem;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.seller-target-value {
  color: #3f4254;
  font-size: 0.95rem;
  font-weight: 700;
}

.seller-progress-track {
  background: #ebedf3;
  height: 8px;
  margin: 0.9rem 0 0.55rem;
}

.seller-progress-fill {
  background: var(--seller-primary);
  min-width: 3px;
}

.seller-progress-fill.completed {
  background: #1bc5bd;
}

.seller-progress-footer {
  color: #7e8299;
  font-size: 0.74rem;
}

.seller-progress-footer strong {
  color: var(--seller-primary);
}

.seller-summary-grid {
  gap: 0.75rem;
  padding: 0.8rem 1.5rem 1rem;
}

.seller-summary-box,
.seller-summary-box.commission,
.seller-summary-box.range {
  background: #f3f6f9;
  border: 1px solid #ebedf3;
  border-radius: 0.42rem;
  padding: 0.85rem 1rem;
}

.seller-summary-box.commission {
  background: color-mix(in srgb, var(--seller-primary) 7%, #ffffff);
  border-color: color-mix(in srgb, var(--seller-primary) 20%, #ffffff);
}

.seller-summary-box.range {
  background: #f3f6f9;
  border-color: #ebedf3;
}

.seller-summary-box > span,
.seller-summary-box small {
  color: #7e8299;
}

.seller-summary-box strong {
  color: #3f4254;
  font-size: 0.98rem;
  font-weight: 700;
}

.seller-summary-box.commission strong,
.seller-summary-box.range strong {
  color: var(--seller-primary);
}

.seller-financial-note {
  background: #fff8dd;
  border: 1px solid #ffeeb5;
  border-radius: 0.42rem;
  color: #895f00;
  gap: 1.25rem;
  justify-content: flex-start;
  margin: 0 1.5rem 1rem;
  padding: 0.7rem 0.9rem;
}

.seller-next-range {
  background: color-mix(in srgb, var(--seller-primary) 5%, #ffffff);
  border: 1px solid color-mix(in srgb, var(--seller-primary) 22%, #ffffff);
  border-left: 3px solid var(--seller-primary);
  border-radius: 0.42rem;
  margin: 0 1.5rem 1rem;
  padding: 0.8rem 0.9rem;
}

.seller-next-icon {
  background: var(--seller-primary);
  border-radius: 0.42rem;
  height: 36px;
  width: 36px;
}

.seller-next-copy > span,
.seller-next-gain > span {
  color: #7e8299;
}

.seller-next-copy > strong {
  color: #3f4254;
  font-size: 0.82rem;
}

.seller-next-copy > small {
  color: #7e8299;
}

.seller-next-gain strong {
  color: var(--seller-primary);
  font-size: 0.95rem;
}

.seller-range-section {
  border-top: 1px solid #ebedf3;
  padding: 1rem 1.5rem 1.25rem;
}

.seller-range-heading {
  margin-bottom: 0.75rem;
}

.seller-range-heading strong {
  color: #3f4254;
  font-weight: 700;
}

.seller-range-heading span {
  color: #7e8299;
}

.seller-range-list {
  gap: 0.65rem;
  grid-auto-columns: minmax(128px, 1fr);
  scrollbar-color: #d8dbe5 transparent;
  scrollbar-width: thin;
}

.seller-range-item {
  background: #f3f6f9;
  border: 1px solid #ebedf3;
  border-radius: 0.42rem;
  min-width: 128px;
  padding: 0.7rem 0.75rem;
}

.seller-range-item > strong {
  color: #3f4254;
}

.seller-range-item > span,
.seller-range-item > small,
.seller-range-status {
  color: #7e8299;
}

.seller-range-item.achieved {
  background: #e8fff3;
  border-color: #c9f7df;
}

.seller-range-item.achieved .seller-range-status {
  color: #1bc5bd;
}

.seller-range-item.current {
  background: color-mix(in srgb, var(--seller-primary) 9%, #ffffff);
  border-color: color-mix(in srgb, var(--seller-primary) 55%, #ffffff);
  box-shadow: inset 3px 0 0 var(--seller-primary);
}

.seller-range-item.current .seller-range-status,
.seller-range-item.current > small {
  color: var(--seller-primary);
}

.seller-range-item.next {
  background: color-mix(in srgb, var(--seller-primary) 5%, #ffffff);
  border-color: color-mix(in srgb, var(--seller-primary) 35%, #ffffff);
}

.seller-range-item.next .seller-range-status,
.seller-range-item.next > small {
  color: var(--seller-primary);
}

.seller-range-item.locked {
  opacity: 0.72;
}

.seller-fixed-rule {
  background: #f3f6f9;
  border-top-color: #ebedf3;
  padding: 1rem 1.5rem;
}

.seller-fixed-rule > svg {
  color: var(--seller-primary);
}

@media (max-width: 991px) {
  .seller-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .seller-summary-box.pace {
    grid-column: 1 / -1;
  }
}

@media (max-width: 767px) {
  .seller-commission-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  .seller-rule-badge {
    display: block;
    margin-left: 3.3rem;
    max-width: calc(100% - 3.3rem);
  }

  .seller-commission-hero {
    padding: 1rem 1rem 0.7rem;
  }

  .seller-sales-value {
    font-size: 1.4rem;
  }

  .seller-progress-footer {
    align-items: flex-start;
    gap: 0.4rem;
  }

  .seller-summary-grid {
    gap: 0.6rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 0.7rem 1rem 1rem;
  }

  .seller-summary-box {
    padding: 0.75rem;
  }

  .seller-summary-box.pace {
    grid-column: 1 / -1;
  }

  .seller-financial-note {
    margin: 0 1rem 1rem;
  }

  .seller-next-range {
    margin: 0 1rem 1rem;
  }

  .seller-range-section {
    padding: 0.9rem 1rem 1rem;
  }

  .seller-range-list {
    display: flex;
    grid-auto-flow: initial;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scroll-snap-type: inline proximity;
  }

  .seller-range-item {
    flex: 0 0 min(220px, 76vw);
    scroll-snap-align: start;
  }
}

@media (max-width: 420px) {
  .seller-title-icon {
    height: 38px;
    width: 38px;
  }

  .seller-commission-header h3 {
    font-size: 1.08rem;
  }

  .seller-rule-badge {
    margin-left: 2.95rem;
    max-width: calc(100% - 2.95rem);
  }

  .seller-progress-copy {
    align-items: flex-start;
  }

  .seller-target-value {
    font-size: 0.82rem;
  }

  .seller-summary-box strong {
    font-size: 0.9rem;
  }
}
</style>
